import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "reactstrap";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { SlidePanel } from "../../Common/Components/Modal/SlidePanel";
import { CustomInput } from "../../Common/Components/Input/CustomInput";
import AdminRequestCostBearerUpdate from "../Models/Request/AdminRequestCostBearerUpdate";
import AdminResponseOrgTypeCompleteModel from "../../Role/Models/Response/AdminResponseOrgTypeCompleteModel";

/**
 * CostBearerUpdatePanel
 *
 * Centered SlidePanel to edit a cost bearer (details + related org types),
 * using the same UI as the "new processing" panel in ReportProceedingList.
 *
 * Props:
 *   isOpen        boolean
 *   costBearerId  string
 *   onClose       () => void
 *   onUpdated     () => void   called after a successful update/reactivation
 */
export function CostBearerUpdatePanel({ isOpen, costBearerId, onClose, onUpdated }) {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();

    const [originalName, setOriginalName] = useState("");
    const [editCostBearer, setEditCostBearer] = useState(new AdminRequestCostBearerUpdate());
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [allOrgTypes, setAllOrgTypes] = useState([]);
    const [orgTypesWithCostBearer, setOrgTypesWithCostBearer] = useState([]);
    const [selectedOrgTypes, setSelectedOrgTypes] = useState([]);

    // ApiCall reports failures via the error callback (after calling the response callback with null)
    const callApi = async (options) => {
        let data = null;
        let error = null;
        await ApiCall(options, (response) => { data = response; }, (message) => { error = message; });
        if (error !== null) throw new Error(error);
        return data;
    };

    // Load data on open, reset on close
    useEffect(() => {
        if (!isOpen || !costBearerId) {
            setOriginalName("");
            setEditCostBearer(new AdminRequestCostBearerUpdate());
            setFieldErrors({});
            setOrgTypesWithCostBearer([]);
            setSelectedOrgTypes([]);
            setSaving(false);
            return;
        }

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const [costBearerResponse, orgTypesResponse] = await Promise.all([
                    callApi({ method: "get", url: `/costbearer?id=${costBearerId}` }),
                    callApi({ method: "get", url: "/role/admin/type/org/list-complete" }),
                ]);
                if (cancelled) return;

                setEditCostBearer(new AdminRequestCostBearerUpdate(costBearerResponse));
                setOriginalName(costBearerResponse?.name ?? "");
                setAllOrgTypes(Array.isArray(orgTypesResponse)
                    ? orgTypesResponse.map(item => new AdminResponseOrgTypeCompleteModel(item))
                    : []);

                if (costBearerResponse?.name) {
                    const byCostBearer = await callApi({
                        method: "get",
                        url: `/role/admin/type/org/list-by-costbearer?costBearerName=${encodeURIComponent(costBearerResponse.name)}`,
                    });
                    if (cancelled) return;
                    const types = Array.isArray(byCostBearer) ? byCostBearer.map(org => org.type).filter(Boolean) : [];
                    setOrgTypesWithCostBearer(types);
                    setSelectedOrgTypes(types);
                }
            } catch (error) {
                console.log('error loading cost bearer', error);
                if (!cancelled) FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, costBearerId]);

    const handleChange = (name, value) => {
        setEditCostBearer(prev => ({ ...prev, [name]: value }));
        setFieldErrors(prev => {
            const copy = { ...prev };
            delete copy[name];
            return copy;
        });
    };

    // Check duplicates via list-all, excluding the current record
    const checkExists = async (field, value) => {
        if (!value) return false;
        const trimmed = value.trim().toLowerCase();
        try {
            const response = await callApi({
                method: "get",
                url: `/costbearer/list-all?query=${encodeURIComponent(value)}`,
            });
            const items = (response && (response.items || response)) || [];
            if (!Array.isArray(items)) return false;
            return items.some(it => {
                if (!it || it.id === editCostBearer.id) return false;
                const compare = field === 'name' ? (it.name || '') : (it.value || '');
                return compare.trim().toLowerCase() === trimmed;
            });
        } catch (e) {
            console.warn("checkExists failed", e);
            return false;
        }
    };

    const handleBlur = async (field) => {
        const value = editCostBearer[field];
        if (!value || value.trim() === "") {
            setFieldErrors(prev => ({ ...prev, [field]: 'validationMessage.fieldRequired' }));
            return;
        }
        if (await checkExists(field, value)) {
            setFieldErrors(prev => ({ ...prev, [field]: field === 'name' ? 'validationMessage.nameExists' : 'validationMessage.kbarExists' }));
        }
    };

    const handleOrgTypeToggle = (orgType, checked) => {
        setSelectedOrgTypes(prev => checked
            ? Array.from(new Set([...prev, orgType]))
            : prev.filter(type => type !== orgType));
    };

    const checkRequired = () =>
        !!editCostBearer.value && editCostBearer.value.trim() !== "" &&
        !!editCostBearer.name && editCostBearer.name.trim() !== "";

    const handleUpdate = async () => {
        if (saving || !checkRequired()) return;

        const schema = AdminRequestCostBearerUpdate.getAttributes(null, true);
        try {
            await schema.validate(editCostBearer, { abortEarly: false });
        } catch (err) {
            const newErrors = {};
            (err?.inner?.length ? err.inner : [err]).forEach(e => {
                if (e?.path) newErrors[e.path] = e.message;
            });
            setFieldErrors(newErrors);
            return;
        }

        setSaving(true);

        const [nameExists, valueExists] = await Promise.all([
            checkExists('name', editCostBearer.name),
            checkExists('value', editCostBearer.value),
        ]);
        if (nameExists || valueExists) {
            setFieldErrors(prev => ({
                ...prev,
                ...(nameExists ? { name: 'validationMessage.nameExists' } : {}),
                ...(valueExists ? { value: 'validationMessage.kbarExists' } : {}),
            }));
            setSaving(false);
            return;
        }

        try {
            // Org types are linked by name, so update them with the name they were loaded with
            const toAdd = selectedOrgTypes.filter(type => !orgTypesWithCostBearer.includes(type));
            const toRemove = orgTypesWithCostBearer.filter(type => !selectedOrgTypes.includes(type));
            if (toAdd.length > 0 || toRemove.length > 0) {
                await callApi({
                    method: "post",
                    url: "/role/admin/type/org/update-costbearers-bulk",
                    data: { costBearerName: originalName, orgTypesToAdd: toAdd, orgTypesToRemove: toRemove },
                });
            }

            await callApi({ method: "put", url: "/costbearer", data: editCostBearer });

            FlashMessage(t('validationMessage.costBearerUpdate'), 'flash-messageSuccess');
            onClose();
            if (onUpdated) onUpdated();
        } catch (error) {
            console.error("Error updating cost bearer", error);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
            setSaving(false);
        }
    };

    const handleReactivate = async () => {
        setSaving(true);
        try {
            const response = await callApi({ method: "put", url: `/costbearer/reactivate?id=${costBearerId}` });
            setEditCostBearer(new AdminRequestCostBearerUpdate(response));
            FlashMessage(t('validationMessage.costBearerReactivated'), 'flash-messageSuccess');
            if (onUpdated) onUpdated();
        } catch (error) {
            console.error("Error reactivating cost bearer", error);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        } finally {
            setSaving(false);
        }
    };

    const renderFieldError = (name) => {
        const err = fieldErrors[name];
        if (!err) return null;
        return (
            <div className="mt-1" style={{ color: '#f56e6e', fontSize: '12px' }}>
                {typeof err === 'string' ? t(err) : err}
            </div>
        );
    };

    const canUpdate = checkRequired() && !saving && Object.keys(fieldErrors).length === 0;

    return (
        <SlidePanel
            isOpen={isOpen}
            onClose={onClose}
            title={t('costBearer.update')}
            variant="center"
        >
            <div className="select-org-panel-wrapper">
                {loading ? (
                    <div className="select-org-panel-loading">
                        <Spinner color="success" size="sm" />
                        <span>{t("sortableList.gettingData")}</span>
                    </div>
                ) : (
                    <>
                        {editCostBearer.deleted && (
                            <div className="d-flex align-items-center justify-content-between gap-2 mb-3 p-3 rounded" style={{ background: '#FCEAEA', color: '#C0392B', fontSize: '13px' }}>
                                <span>{t('validationMessage.modalReactivate')}</span>
                                <button onClick={handleReactivate} disabled={saving} className="select-org-panel-btn-confirm enabled flex-grow-0 px-3 py-2">
                                    {t('button.reactivate')}
                                </button>
                            </div>
                        )}

                        {/* KBAR */}
                        <div className="mb-3">
                            <CustomInput
                                model={AdminRequestCostBearerUpdate}
                                attribute="value"
                                data={editCostBearer.value}
                                customChange={(e) => handleChange('value', e?.target?.value ?? "")}
                                inputProps={{ id: 'value', onBlur: () => handleBlur('value') }}
                            />
                            {renderFieldError('value')}
                        </div>

                        {/* Name */}
                        <div className="mb-3">
                            <CustomInput
                                model={AdminRequestCostBearerUpdate}
                                attribute="name"
                                data={editCostBearer.name}
                                customChange={(e) => handleChange('name', e?.target?.value ?? "")}
                                inputProps={{ id: 'name', onBlur: () => handleBlur('name') }}
                            />
                            {renderFieldError('name')}
                        </div>

                        {/* Message */}
                        <div className="mb-3">
                            <label htmlFor="costBearerMessage" className="select-org-panel-label">
                                {t('costBearer.message')}
                            </label>
                            <textarea
                                id="costBearerMessage"
                                className="form-control"
                                value={editCostBearer.message ?? ""}
                                onChange={(e) => handleChange('message', e.target.value)}
                                style={{ resize: "vertical", minHeight: "80px", height: "auto" }}
                                rows={3}
                            />
                            {renderFieldError('message')}
                        </div>

                        {/* Included in AGI */}
                        <div className="rpv2-multiday-row mb-4">
                            <input
                                type="checkbox"
                                id="costBearerIncludedInAGI"
                                className="rpv2-multiday-checkbox"
                                checked={!!editCostBearer.includedInAGI}
                                onChange={(e) => handleChange('includedInAGI', e.target.checked)}
                            />
                            <label htmlFor="costBearerIncludedInAGI" className="rpv2-multiday-label">
                                {t('costBearer.includedInAGILabel')}
                            </label>
                        </div>

                        {/* Related org types */}
                        {allOrgTypes.length > 0 && (
                            <div className="mb-4">
                                <hr className="mt-0 mb-3" />
                                <p className="select-org-panel-intro mb-2">{t('costBearer.relatedOrgs')}</p>
                                <div className="row g-0">
                                    {allOrgTypes.map((orgType) => {
                                        const inputId = `orgType-${(orgType.type || '').replace(/\s+/g, '-')}`;
                                        return (
                                            <div className="col-6 rpv2-multiday-row mt-0 mb-2 pe-2" key={orgType.type}>
                                                <input
                                                    type="checkbox"
                                                    id={inputId}
                                                    className="rpv2-multiday-checkbox"
                                                    checked={selectedOrgTypes.includes(orgType.type)}
                                                    disabled={saving}
                                                    onChange={(e) => handleOrgTypeToggle(orgType.type, e.target.checked)}
                                                />
                                                <label htmlFor={inputId} className="rpv2-multiday-label" style={{ wordBreak: 'break-word' }}>
                                                    {orgType.type}
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="select-org-panel-buttons">
                            <button onClick={onClose} className="select-org-panel-btn-cancel">
                                {t('button.cancel') || "Avbryt"}
                            </button>
                            <button
                                onClick={handleUpdate}
                                disabled={!canUpdate}
                                className={`select-org-panel-btn-confirm ${canUpdate ? "enabled" : "disabled"}`}
                            >
                                {saving
                                    ? <Spinner size="sm" />
                                    : (t('button.update') || "Uppdatera")}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </SlidePanel>
    );
}
